import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Clock3, Pencil, X } from 'lucide-react';
import { api, formatMoney, type Environment, type Session } from '../api';
import type { JsonDTO } from '../../../src/domain/json';
import type { NeedsYouView } from '../../../src/application/needs-you';
import type { DataIssue } from '../../../src/application/review';
import { Button } from '../components/ui/button';
import { DraftEditor } from '../components/DraftEditor';
import { useWorkDecisions, type WorkItem } from '../components/work-item';
import { actionLabel } from '../needs-you';
import { useAgentName } from '../agent-name';
import { plainDates, splitQuestion } from '../review-text';
import './simple-review.css';

/**
 * Review in the simple view (UI_MODE=simple, AIprise 2026-11-02). Two lists: emails to approve, and everything else that
 * needs a person (questions, replies, tasks). One line per row; the panel shows one thing to decide and only the buttons
 * for it. Ledger problems (a due date before the issue date) are said once at the top, and their tasks are left out of the
 * list. Every decision goes through the same routes and checks as the full Collections page (useWorkDecisions).
 */
type View = JsonDTO<NeedsYouView>;
type Tab = 'emails' | 'questions';
const quickReply = ['check_payment', 'open_dispute', 'draft_response', 'delay'];

const money = (item: WorkItem) => item.amount ? formatMoney(item.amount.minor, item.amount.currency) : '';

export function SimpleReview({ session }: { session: Session }) {
  const agent = useAgentName();
  const [view, setView] = useState<View | null>(null); const [environment, setEnvironment] = useState<Environment | null>(null);
  const [issues, setIssues] = useState<JsonDTO<DataIssue>[]>([]);
  const [error, setError] = useState(''); const [tab, setTab] = useState<Tab>('emails');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const canDecide = session.actor.roles.some(role => role === 'owner' || role === 'approver');

  const reload = useCallback(async () => {
    const [next, env, data] = await Promise.all([api<View>('/app-api/needs-you'), api<Environment>('/app-api/environment'),
      api<{ issues: JsonDTO<DataIssue>[] }>('/app-api/review/data-issues')]);
    setView(next); setEnvironment(env); setIssues(data.issues); setError('');
  }, []);
  useEffect(() => {
    const refresh = () => { void reload().catch(e => setError(e instanceof Error ? e.message : 'This page could not load.')); };
    refresh(); window.addEventListener('alder:state-changed', refresh);
    return () => window.removeEventListener('alder:state-changed', refresh);
  }, [reload]);

  const items = useMemo(() => view?.items ?? [], [view]);
  const work = useWorkDecisions({ items, actorId: session.actor.id, shadow: view?.delivery.shadow ?? false, environment, reload });
  const badInvoices = useMemo(() => new Set(issues.flatMap(i => i.invoices.map(v => v.id))), [issues]);
  // A task about invoices whose dates are wrong is the data problem again; the banner says it once.
  const dataNoise = (item: WorkItem) => item.kind === 'task' && item.invoices.length > 0 && item.invoices.every(i => badInvoices.has(i.id));
  const emails = items.filter(i => i.kind === 'approval');
  const questions = items.filter(i => i.kind !== 'approval' && !dataNoise(i));
  const list = tab === 'emails' ? emails : questions;
  const open = items.find(i => i.key === openKey) ?? null;
  useEffect(() => { if (openKey && !open) setOpenKey(null); }, [openKey, open]);
  const choose = (key: string) => { work.clear(); setOpenKey(key); };

  const issueCustomers = [...new Set(issues.flatMap(i => i.invoices.map(v => v.customerName)))];
  const issueCount = issues.reduce((n, i) => n + i.invoices.length, 0);

  return <main id="main-content" className="sr-page">
    <header className="sr-head"><h1>Review</h1>
      <p>{view?.delivery.shadow ? `Nothing is emailed yet. Approve what you would send; ${agent} learns from your edits.`
        : `${agent} sends an email only after you approve it.`}</p></header>
    {error && <div className="alert error" role="alert">{error}</div>}
    {issueCount > 0 && <div className="sr-banner" role="note"><AlertTriangle size={17} aria-hidden/>
      <div><strong>{issueCount} invoice{issueCount === 1 ? ' has' : 's have'} a due date before the issue date</strong>
        <span>{issueCustomers.slice(0, 4).join(', ')}{issueCustomers.length > 4 ? ` and ${issueCustomers.length - 4} more` : ''}.
          Fix the dates in your billing system; {agent} holds these until then.</span></div></div>}
    <div className="sr-tabs" role="tablist" aria-label="What needs you">
      <button type="button" role="tab" aria-selected={tab === 'emails'} onClick={() => setTab('emails')}>
        Emails to approve <span>{emails.length}</span></button>
      <button type="button" role="tab" aria-selected={tab === 'questions'} onClick={() => setTab('questions')}>
        Questions <span>{questions.length}</span></button>
    </div>
    {work.notice && <p className="sr-notice" role="status">{work.notice}</p>}
    {work.error && <div className="alert error" role="alert">{work.error}</div>}
    <div className={`sr-layout${open ? ' sr-has-panel' : ''}`}>
      <ul className="sr-list" aria-label={tab === 'emails' ? 'Emails to approve' : 'Questions'}>
        {view && list.length === 0 && <li className="sr-empty">{tab === 'emails' ? 'No emails waiting for you.' : `${agent} has no questions for you.`}</li>}
        {list.map(item => <li key={item.key}>
          <button type="button" className="sr-row" aria-current={item.key === openKey || undefined} onClick={() => choose(item.key)}>
            <strong>{item.customerName}</strong>
            <span className="sr-line">{plainDates(rowLine(item))}</span>
            <span className="sr-money">{money(item)}</span>
          </button></li>)}
      </ul>
      {open && <aside className="sr-panel" aria-labelledby="sr-panel-title">
        <header><h2 id="sr-panel-title">{open.customerName}</h2>
          <Button variant="ghost" size="icon" aria-label="Close" onClick={() => setOpenKey(null)}><X aria-hidden/></Button></header>
        {open.kind === 'approval' ? <EmailPanel item={open} work={work} canDecide={canDecide}/>
          : open.kind === 'reply' ? <ReplyPanel item={open} work={work} canDecide={canDecide} agent={agent}/>
            : <QuestionPanel item={open} work={work} canDecide={canDecide} agent={agent}/>}
        {open.customerId && <a className="sr-file" href={`#customers/${encodeURIComponent(open.customerId)}`}>Open the customer</a>}
      </aside>}
    </div>
  </main>;
}

function rowLine(item: WorkItem) {
  if (item.kind === 'approval') return item.want;
  if (item.kind === 'reply') return item.reply?.summary || item.reply?.subject || 'Replied';
  return splitQuestion(item.task?.title ?? item.want).question;
}

type PanelProps = { item: WorkItem; work: ReturnType<typeof useWorkDecisions>; canDecide: boolean };

function EmailPanel({ item, work, canDecide }: PanelProps) {
  const draft = item.approval!.draft;
  const voted = work.voted(item);
  return <>
    <dl className="sr-email-head"><div><dt>To</dt><dd>{draft.to.join(', ')}</dd></div>
      <div><dt>Subject</dt><dd>{draft.subject}</dd></div></dl>
    {work.editing ? <DraftEditor key={`${item.id}:${item.revision}`} subject={draft.subject} body={draft.body} busy={work.busy}
      onSave={(body, subject) => void work.saveEdit(item, body, subject)} onCancel={() => work.setEditing(false)}/>
      : <div className="sr-email-body">{draft.body.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}</div>}
    {canDecide && !work.editing && <footer className="sr-actions">
      <Button variant="ghost" disabled={work.busy} onClick={() => void work.decideApproval(item, 'reject')}><X aria-hidden/>Reject</Button>
      <Button variant="outline" disabled={work.busy} onClick={() => work.setEditing(true)}><Pencil aria-hidden/>Edit</Button>
      <Button disabled={work.busy || voted} onClick={() => void work.decideApproval(item, 'approve')}><Check aria-hidden/>
        {voted ? 'Approved' : 'Approve'}</Button></footer>}
  </>;
}

function ReplyPanel({ item, work, canDecide, agent }: PanelProps & { agent: string }) {
  const reply = item.reply!;
  const options = reply.recommendedActions.filter(a => quickReply.includes(a));
  return <>
    <section className="sr-said"><h3>They wrote</h3><blockquote>{reply.text}</blockquote></section>
    {item.recommendation.reason && <p className="sr-suggest"><strong>{agent} suggests</strong> {plainDates(item.recommendation.reason)}</p>}
    {canDecide && <footer className="sr-actions">
      {options.length === 0 && <a className="sr-file" href="#inbox">Answer in the inbox</a>}
      {options.map(kind => <Button key={kind} variant={kind === item.recommendation.action ? 'default' : 'outline'} disabled={work.busy}
        onClick={() => void work.decideReply(item, kind)}>{actionLabel(kind)}</Button>)}</footer>}
  </>;
}

function QuestionPanel({ item, work, canDecide, agent }: PanelProps & { agent: string }) {
  const { question, suggestion } = splitQuestion(item.task?.title ?? item.want);
  const advice = suggestion || (item.recommendation.reason !== question ? item.recommendation.reason : '');
  return <>
    <p className="sr-question">{plainDates(question)}</p>
    {advice && <p className="sr-suggest"><strong>{agent} suggests</strong> {plainDates(advice)}</p>}
    {canDecide && <footer className="sr-actions">
      <Button variant="outline" disabled={work.busy} onClick={() => void work.decideTask(item, 'snooze')}><Clock3 aria-hidden/>Later</Button>
      <Button disabled={work.busy} onClick={() => void work.decideTask(item, 'done')}><Check aria-hidden/>Done</Button></footer>}
  </>;
}
